import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const DEVICE_ID_KEY = "sux1_device_id";

const GuestContext = createContext(null);

/** RFC 4122 v4 UUID (crypto-random). Falls back to a Math.random-based
 *  variant in the unlikely event `crypto` is unavailable. The server-side
 *  _DEVICE_ID_RE only requires UUID-shaped strings — exact RFC compliance
 *  is not critical. */
function generateDeviceId() {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40; // v4
      bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
      const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0"));
      return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
    }
  } catch {
    /* ignore */
  }
  // Last-ditch fallback — not cryptographically secure, but still UUID-shaped.
  const rand = () => Math.floor(Math.random() * 0xffff).toString(16).padStart(4, "0");
  return `${rand()}${rand()}-${rand()}-4${rand().slice(1)}-8${rand().slice(1)}-${rand()}${rand()}${rand()}`;
}

export const GuestProvider = ({ children }) => {
  const [deviceId, setDeviceId] = useState(() => {
    try {
      const existing = localStorage.getItem(DEVICE_ID_KEY);
      if (existing) return existing;
      const fresh = generateDeviceId();
      localStorage.setItem(DEVICE_ID_KEY, fresh);
      return fresh;
    } catch {
      return generateDeviceId();
    }
  });

  // Keep the device_id persistent — never reset it on login (we want the
  // anonymous history to migrate on signup, and after login the id just
  // becomes dormant since the Bearer token takes precedence server-side).
  useEffect(() => {
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    } catch {
      /* ignore storage errors (private mode, etc.) */
    }
  }, [deviceId]);

  const value = useMemo(() => ({ deviceId }), [deviceId]);
  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
};

export const useGuest = () => {
  const ctx = useContext(GuestContext);
  if (!ctx) throw new Error("useGuest must be used inside GuestProvider");
  return ctx;
};

/** Read the current device_id without a hook (for callers outside React tree,
 *  e.g. axios interceptors). Always returns a non-empty UUID — generates and
 *  persists one if missing. */
export function getStoredDeviceId() {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const fresh = generateDeviceId();
    localStorage.setItem(DEVICE_ID_KEY, fresh);
    return fresh;
  } catch {
    return generateDeviceId();
  }
}
